import React, { useEffect, useMemo, useState, useRef } from "react";
import ContactDetails from "../ContactDetails/ContactDetails";
import InsightPanel from "../InsightPanel/InsightPanel";
import ActionPanel from "../ActionPanel/ActionPanel";
import "./DetailsDashboard.css";
import { useDispatch, useSelector } from "react-redux";
import { getDetailsPageDataInit } from "../../../redux/slices/operationHomeSlice";
import { useLocation, useNavigate } from "react-router-dom";
import { encodeId } from "../../Helper/encdec";

const Dashboard = ({
  memberId,
  callId,
  onCurrentCallInfo,
  onReviewContext,
  openScoringSignal,
  navigationDisabled = false,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const actionPanelRef = useRef(null);
  const [showActionPanel, setShowActionPanel] = useState(false);

  const memberDetails = useSelector(
    (state) => state.operationHome.details.data,
  );
  const size = useSelector((state) => state.operationHome.details.size);

  // Check if navigated from notification and open ActionPanel
  useEffect(() => {
    if (location.state?.openActionPanel) {
      setShowActionPanel(true);
      // Scroll to ActionPanel after a short delay to ensure DOM is ready
      setTimeout(() => {
        if (actionPanelRef.current) {
          actionPanelRef.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 300);
    }
  }, [location.state?.openActionPanel]);

  useEffect(() => {
    dispatch(getDetailsPageDataInit(memberId));
  }, [dispatch, memberId]);

  // Group by CallId to keep all prompt/response entries together, sorted by
  // meeting date (oldest → newest) — this is the order the Meeting Date arrows
  // walk through.
  const groupedCalls = useMemo(() => {
    if (!Array.isArray(memberDetails?.callDetails)) return null;

    const callsByCallId = memberDetails.callDetails.reduce((acc, current) => {
      const cid = current.CallId;
      if (!acc[cid]) {
        acc[cid] = {
          CallId: cid,
          meetingDate: current.meetingDate,
          meetingPerson: current.meetingPerson,
          callCategory: current.callCategory,
          callType: current.callType,
          duration: current.duration,
          status: current.status,
          rating: current.rating,
          items: [], // This will hold all prompt/response entries for this call
        };
      }
      // Add the current item to the call's items array
      acc[cid].items.push(current);
      return acc;
    }, {});

    return Object.values(callsByCallId).sort((a, b) => {
      if (a.meetingDate < b.meetingDate) return -1;
      if (a.meetingDate > b.meetingDate) return 1;
      return 0;
    });
  }, [memberDetails]);

  // The URL is the single source of truth for which call is on screen. Both the
  // Meeting Date arrows below and the top InsightNavigator navigate, so the
  // address bar and this panel can never drift apart — a drift used to make the
  // navigator's arrows silently no-op whenever their target happened to be the
  // call still named in the (stale) URL.
  const currentIndex = useMemo(() => {
    if (!groupedCalls?.length || !callId) return -1;
    return groupedCalls.findIndex((call) => call.CallId === callId);
  }, [groupedCalls, callId]);

  // `-1` means the loaded member data doesn't contain this call yet (i.e. a
  // cross-member jump whose fetch is still in flight). Render nothing for the
  // call rather than an unrelated one; the effect above refills it.
  const currentCall = currentIndex >= 0 ? groupedCalls[currentIndex] : null;

  // Surface the currently-viewed call's display info up to the page so the top
  // navigator can render its header correctly even when this call isn't in the
  // navigator's own list. Keeps the header and this panel in sync.
  useEffect(() => {
    if (!onCurrentCallInfo || !currentCall) return;
    onCurrentCallInfo({
      callId: currentCall.CallId,
      memberId: memberDetails?.CustomerID ?? memberId,
      memberName: memberDetails?.FirstName ?? "",
      callCategory: currentCall.callCategory ?? "",
      agentName: currentCall.meetingPerson ?? "",
      engagementDate1: currentCall.meetingDate ?? "",
    });
  }, [currentCall, memberDetails, memberId, onCurrentCallInfo]);

  // Prepare section object for ContactDetails
  const sectionForContact = currentCall
    ? {
        meetingDate: currentCall.meetingDate,
        meetingPerson: currentCall.meetingPerson,
        items: currentCall.items || [], // Pass all items for this call
      }
    : {};

  const currentStatus = currentCall?.status?.toLowerCase();

  const shouldRenderActionPanel = [
    "yes",
    "in-progress",
    "closed",
    "no",
  ].includes(currentStatus);

  // Handle section change from the ContactDetails (Meeting Date) arrows.
  // Ignored while a PDF download is in flight (see DetailsScreen.js) so
  // switching calls mid-generation can't redirect the download to the wrong
  // call's content.
  const handleSectionChange = (newIndexOrUpdater) => {
    if (navigationDisabled) return;
    // No resolved position yet (member data still loading) — a relative step
    // would land on an arbitrary call, so refuse it.
    if (!groupedCalls?.length || currentIndex < 0) return;

    // Resolve to a numeric index
    const newIndex =
      typeof newIndexOrUpdater === "function"
        ? newIndexOrUpdater(currentIndex) // call it with currentIndex
        : newIndexOrUpdater;

    if (
      !Number.isInteger(newIndex) ||
      newIndex < 0 ||
      newIndex >= groupedCalls.length
    ) {
      return;
    }

    const nextCallId = groupedCalls[newIndex]?.CallId;
    if (!nextCallId || nextCallId === callId) return;

    // Drive the URL — see the `currentIndex` note above.
    const encodedMemberId = encodeId(String(memberId).replace(/\s+/g, ""));
    navigate(`/detailsPage/${encodedMemberId}/${encodeId(nextCallId)}`);
  };

  return (
    <div
      className={`dashboard-grid ${
        shouldRenderActionPanel
          ? showActionPanel
            ? "with-action-panel"
            : "collapsed-action-panel"
          : "no-action-panel"
      }`}
    >
      <ContactDetails
        memberId={memberId}
        memberDetails={memberDetails}
        section={sectionForContact}
        sectionLength={groupedCalls?.length}
        setSection={handleSectionChange}
        currentSection={currentIndex}
        callId={callId}
        size={size}
        navigationDisabled={navigationDisabled || currentIndex < 0}
      />

      <InsightPanel
        memberId={memberId}
        memberDetails={memberDetails}
        section={sectionForContact}
        callId={callId}
        shouldRenderActionPanel={shouldRenderActionPanel}
        onReviewContext={onReviewContext}
        openScoringSignal={openScoringSignal}
      />

      {shouldRenderActionPanel && (
        <div ref={actionPanelRef}>
          <ActionPanel
            memberId={memberId}
            callId={callId}
            show={showActionPanel}
            setShow={setShowActionPanel}
          />
        </div>
      )}
    </div>
  );
};

export default Dashboard;
